interface IResult {
  trafficType: "direct" | "paid" | "";
  keyword: string;
  searchEngine: string;
  landingPageTitle: string;
  landingPageUrl: string;
  /**
   * SEO特殊路由
   */
  seoPathNames: RegExp[];
  isAppointSEO: boolean;
}

interface Iengines {
  domains: string[];
  name: string;
  params: string[];
}

function getSearchParams(key: string) {
  const params = new URLSearchParams(window.location.search);
  return params.get(key);
}

// 检测搜索引擎信息
function detectSearchEngine(engines: Iengines[], result: IResult, url: URL, referrer?: URL) {
  const referrerHost = referrer?.hostname.toLowerCase();
  const engine = engines.find((e) => e.domains.some((d) => referrerHost?.includes(d)));

  if (!engine) return;

  const referrerParams = referrer?.searchParams;
  const urlParams = url.searchParams;
  const referrerKeyword = engine.params.find((p) => referrerParams?.has(p));
  const urlKeyword = engine.params.find((p) => urlParams?.has(p));

  result.searchEngine = engine.name;
  result.keyword = referrerKeyword || urlKeyword || "";
}

//检测是否为自然流量
function identifyTraffic(result: IResult, url: URL, referrer?: URL) {
  if (
    url.searchParams.has("gclid") || // Google Ads
    url.searchParams.has("msclkid") || // Microsoft Advertising
    url.searchParams.has("bd_vid") || // Baidu Ads (关键补充)
    url.searchParams.has("utm_medium") ||
    url.pathname.endsWith(".php")
  ) {
    result.trafficType = "paid";
  }

  if (
    referrer?.searchParams.has("gclid") || // Google Ads
    referrer?.searchParams.has("msclkid") || // Microsoft Advertising
    referrer?.searchParams.has("bd_vid") || // Baidu Ads (关键补充)
    referrer?.searchParams.has("utm_medium") ||
    referrer?.pathname.endsWith(".php")
  ) {
    result.trafficType = "paid";
  }

  if (result.searchEngine?.length) {
    result.trafficType = "direct";
  }
}

export function getSafeKey(key: string): number | undefined {
  // 渠道 code
  const safeNumber = getSearchParams(key);
  // 仅允许原始类型为 number 或 string 的输入
  if (typeof safeNumber !== "number" && typeof safeNumber !== "string") {
    return undefined;
  }
  const num = Number(safeNumber);
  // 32 位有符号整数范围
  const INT_MIN = -2147483648;
  const INT_MAX = 2147483647;
  // 校验：非 NaN、有限数值、整数且在 int 范围内
  if (!isNaN(num) && isFinite(num) && num >= INT_MIN && num <= INT_MAX) {
    return num;
  }
  return undefined;
}

// 渠道 code
export function getSafePlatformCode() {
  return getSafeKey("platformCode");
}

/**
 * 分析搜索引擎流量，并返回首次访问的相关信息。
 * 增加了对百度付费广告参数的识别，以提高准确性。
 *
 * @returns {IResult|null} 如果是首次来自搜索引擎的访问，则返回一个包含流量信息的对象，否则返回存储的信息或null。
 */
export function analyzeSearchTraffic(): IResult | null {
  const engines = [
    { domains: ["baidu"], name: "百度", params: ["wd", "word", "q", "keyword", "query"] },
    { domains: ["google"], name: "谷歌", params: ["wd", "word", "q", "keyword", "query"] },
    { domains: ["bing"], name: "必应", params: ["wd", "word", "q", "keyword", "query"] },
    { domains: ["sogou"], name: "搜狗", params: ["wd", "word", "q", "keyword", "query"] },
    { domains: ["so.com"], name: "360搜索", params: ["wd", "word", "q", "keyword", "query"] },
    { domains: ["quark"], name: "夸克", params: ["wd", "word", "q", "keyword", "query", "text"] },
    { domains: ["yandex.com"], name: "Yandex", params: ["wd", "word", "q", "keyword", "query", "text"] },
    { domains: ["deepseek"], name: "deepseek", params: ["wd", "word", "q", "keyword", "query", "text"] },
    { domains: ["yuanbao", "tencent"], name: "腾讯,元宝", params: ["wd", "word", "q", "keyword", "query", "text"] },
    { domains: ["feishu"], name: "飞书", params: ["wd", "word", "q", "keyword", "query", "text"] }
  ];

  try {
    const result: IResult = {
      trafficType: "", // 流量类型: direct/paid
      keyword: "", // 搜索关键词
      searchEngine: "", // 搜索引擎名称
      landingPageTitle: document.title, // 落地页标题
      landingPageUrl: window.location.href, // 完整URL
      seoPathNames: [/\/model\/\d+/, /\/image\/\d+/, /aiAcademy/],
      isAppointSEO: false
    };

    const pageUrl = new URL(decodeURIComponent(window.location.href));
    const referrerUrl = document.referrer ? new URL(decodeURIComponent(document.referrer)) : undefined;

    const hasSEO = result.seoPathNames.find((item) => item.test(pageUrl.pathname));

    if (hasSEO) {
      result.isAppointSEO = true;
    }

    //先确定搜索引擎的名称以及关键词
    detectSearchEngine(engines, result, pageUrl, referrerUrl);

    identifyTraffic(result, pageUrl, referrerUrl);

    return result;
  } catch (e) {
    console.error("分析搜索流量时出错:", e);
    return null;
  }
}

/**
 * 获取url以及doc文档信息识别参数来源
 * @returns
 */
export function getSource() {
  const source = getSearchParams("source");
  const traffic = analyzeSearchTraffic();

  if (!source) {
    if (traffic?.trafficType == "direct") {
      return "11c2a9658a84be009b007ad8b631b8a1";
    } else if (traffic?.trafficType == "paid") {
      return "93c1a1c42ce945e4c89ff02ad72fc89e";
    }
  } else {
    if (source === "works_share") {
      return "281cd8d7daf8668d547b6541a11538a0";
    } else if (source === "personal_model_share") {
      return "4f65ea37903481b014839e7caa4398af";
    }
  }
  return source as string;
}
